// Date-only values ("YYYY-MM-DD": Postgres `date` columns such as birthday /
// fulfillment_date / start_date, and <input type="date"> values) are calendar
// days, not instants. `new Date('1980-09-03')` parses them as UTC midnight, so
// any viewer west of Greenwich sees the PREVIOUS day (the birthday off-by-one
// that keeps coming back — see also the CSV fix in 8d647a06). Every read or
// render of a date-only value goes through here; true instants (timestamptz)
// keep using timezoneUtils (instantToWallClock / formatInstantInTimeZone).

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

const pad = (value) => String(value).padStart(2, '0');

/** True when `value` is a bare "YYYY-MM-DD" string (surrounding whitespace ok). */
export function isDateOnly(value) {
  return typeof value === 'string' && DATE_ONLY_RE.test(value.trim());
}

/**
 * Calendar parts of a date-only value: { year, month (1-12), day }.
 * Accepts a "YYYY-MM-DD" string (a longer ISO string is NOT accepted — that is
 * an instant and must be zone-converted by the caller) or a Date, whose LOCAL
 * parts are read. Returns null for anything else or an impossible date.
 */
export function dateOnlyParts(value) {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return { year: value.getFullYear(), month: value.getMonth() + 1, day: value.getDate() };
  }
  if (typeof value !== 'string') return null;
  const match = value.trim().match(DATE_ONLY_RE);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  // Reject e.g. 2026-02-30 rather than letting the Date constructor roll it over.
  const probe = new Date(year, month - 1, day);
  if (probe.getFullYear() !== year || probe.getMonth() !== month - 1 || probe.getDate() !== day) return null;
  return { year, month, day };
}

/** Local-midnight Date for a date-only value (or null). Never shifts by zone. */
export function parseDateOnly(value) {
  const parts = dateOnlyParts(value);
  if (!parts) return null;
  return new Date(parts.year, parts.month - 1, parts.day);
}

/**
 * "YYYY-MM-DD" from a Date's LOCAL calendar day (the inverse of parseDateOnly).
 * Replaces `d.toISOString().slice(0, 10)`, which reads the UTC day and is
 * already tomorrow late in a US evening. A date-only string passes through.
 */
export function toDateOnly(value) {
  const parts = dateOnlyParts(value);
  if (!parts) return '';
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`;
}

/**
 * Render a date-only value with Intl, e.g. "Sep 3, 1980", without a zone shift.
 * Returns '' when the value is not a date-only string / Date.
 */
export function formatDateOnly(
  value,
  options = { month: 'short', day: 'numeric', year: 'numeric' },
  locale = 'en-US',
) {
  const date = parseDateOnly(value);
  if (!date) return '';
  return date.toLocaleDateString(locale, options);
}

/** "MM/DD/YYYY" for a date-only value, '' otherwise. */
export function formatDateOnlyMMDDYYYY(value) {
  const parts = dateOnlyParts(value);
  if (!parts) return '';
  return `${pad(parts.month)}/${pad(parts.day)}/${parts.year}`;
}

/**
 * Whole years between a date-only birthday and a calendar day (`on` is a
 * date-only string or a Date read on the local clock). Null when either side
 * is unusable.
 */
export function ageOn(dob, on) {
  const b = dateOnlyParts(dob);
  const o = dateOnlyParts(on);
  if (!b || !o) return null;
  let age = o.year - b.year;
  if (o.month < b.month || (o.month === b.month && o.day < b.day)) age -= 1;
  return age;
}

/**
 * The anniversary of a date-only value in `year`, as a local-midnight Date.
 * Feb 29 falls back to Feb 28 in a non-leap year.
 */
export function anniversaryInYear(value, year) {
  const parts = dateOnlyParts(value);
  if (!parts) return null;
  const candidate = new Date(year, parts.month - 1, parts.day);
  if (candidate.getMonth() !== parts.month - 1) {
    return new Date(year, parts.month - 1, parts.day - 1);
  }
  return candidate;
}

/**
 * Whole calendar days from one date-only value to another (negative when
 * `to` is earlier), or null when either is not a calendar day. Counted on the
 * calendar, not in milliseconds: local midnights are 25 hours apart across
 * the autumn clock change, which made a day exactly a week out read as "more
 * than 7 days away".
 */
export function daysBetweenDateOnly(from, to) {
  const a = dateOnlyParts(from);
  const b = dateOnlyParts(to);
  if (!a || !b) return null;
  return Math.round((Date.UTC(b.year, b.month - 1, b.day) - Date.UTC(a.year, a.month - 1, a.day)) / 86400000);
}

/** Local midnight of the calendar day containing `date` (Date or date-only). */
export function startOfLocalDay(date) {
  return parseDateOnly(date);
}

/** Last millisecond of the local calendar day containing `date`. */
export function endOfLocalDay(date) {
  const parts = dateOnlyParts(date);
  if (!parts) return null;
  return new Date(parts.year, parts.month - 1, parts.day, 23, 59, 59, 999);
}
